/**
 * HotelModal.jsx - 호텔 신규/수정 모달 (Phase 3-C, 2026-04-30)
 */
import React, { useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { validateReservationForm } from '../reservationFormValidation';
import { useReservationFieldSettings } from '../useReservationFieldSettings';
import { Field, Modal, Grid } from './_modal-helpers';
import HotelNightlyRatesEditor, { defaultNightlyRates, hasNightlyAmount, hotelTotal, normalizeNightlyRates } from './HotelNightlyRatesEditor';

function ymd(v) {
  if (!v) return null;
  if (v instanceof Date) return `${v.getFullYear()}-${String(v.getMonth()+1).padStart(2,'0')}-${String(v.getDate()).padStart(2,'0')}`;
  return String(v).slice(0, 10);
}

export default function HotelModal({ travelCode, row, onClose, onSaved }) {
  const isNew = !row;
  const savedRates = normalizeNightlyRates(row?.nightly_rates, { minRows: 0, keepEmpty: false });
  const hasSavedRates = hasNightlyAmount(savedRates);
  const [useNightlyRates, setUseNightlyRates] = useState(isNew || hasSavedRates);
  const [form, setForm] = useState(row ? {
    ...row,
    check_in: ymd(row.check_in),
    check_out: ymd(row.check_out),
    nightly_rates: hasSavedRates ? normalizeNightlyRates(row.nightly_rates, { minRows: 2 }) : [],
  } : { nightly_rates: defaultNightlyRates() });
  const [saving, setSaving] = useState(false);
  const { hiddenSet } = useReservationFieldSettings();
  const showField = (key) => !hiddenSet.has(key);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const updateRates = (rates) => setForm((f) => ({ ...f, nightly_rates: rates, total_cost: hotelTotal(rates) || null }));

  function convertToNightlyRates() {
    setUseNightlyRates(true);
    setForm((f) => ({ ...f, nightly_rates: defaultNightlyRates(), total_cost: null }));
  }

  async function handleSave() {
    const nightlyRates = useNightlyRates
      ? normalizeNightlyRates(form.nightly_rates, { minRows: 0, keepEmpty: false })
      : [];
    const payloadForm = useNightlyRates
      ? { ...form, nightly_rates: nightlyRates, total_cost: nightlyRates.length ? hotelTotal(nightlyRates) : null }
      : { ...form, nightly_rates: [] };
    const validationMessage = validateReservationForm({
      master: { travel_code: travelCode, customer_name: '-', reservation_date: '-' },
      flights: [],
      cars: [],
      hotels: [payloadForm],
      isVisible: showField,
    });
    if (validationMessage) { alert(validationMessage); return; }

    setSaving(true);
    try {
      const { notes, ...payload } = payloadForm;
      const r = isNew
        ? await apiClient.post(`/api/admin/reservations/${encodeURIComponent(travelCode)}/hotels`, payload)
        : await apiClient.put(`/api/admin/hotel-bookings/${row.id}`, payload);
      if (r.ok && r.data?.success) { onSaved && onSaved(); onClose(); }
      else alert(r.data?.message || '저장 실패');
    } catch (e) { alert('오류: ' + e.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={isNew ? '호텔 추가' : `호텔 수정 #${row.id}`} onClose={onClose} onSave={handleSave} saving={saving}>
      <Grid>
        {showField('hotel.total_cost') && <Field label="총액" required type="amount" value={form.total_cost} onChange={upd('total_cost')} />}
        {showField('hotel.hotel_name') && <Field label="호텔명" value={form.hotel_name} onChange={upd('hotel_name')} />}
        {showField('hotel.hotel_address') && <Field label="주소" value={form.hotel_address} onChange={upd('hotel_address')} />}
        {showField('hotel.room_type') && <Field label="객실 타입" value={form.room_type} onChange={upd('room_type')} />}
        {showField('hotel.room_count') && <Field label="객실 수" type="number" value={form.room_count} onChange={upd('room_count')} />}
        {showField('hotel.check_in') && <Field label="체크인" type="date" value={form.check_in} onChange={upd('check_in')} />}
        {showField('hotel.check_out') && <Field label="체크아웃" type="date" value={form.check_out} onChange={upd('check_out')} />}
        {showField('hotel.nights') && <Field label="박" type="number" value={form.nights} onChange={upd('nights')} />}
        {showField('hotel.guest_count') && <Field label="투숙객" type="number" value={form.guest_count} onChange={upd('guest_count')} />}
        {showField('hotel.confirmation_no') && <Field label="확정번호" value={form.confirmation_no} onChange={upd('confirmation_no')} />}
      </Grid>
      {(showField('hotel.total_cost') || showField('hotel.nightly_rates')) && (
        useNightlyRates ? (
          <HotelNightlyRatesEditor rates={form.nightly_rates} onChange={updateRates} />
        ) : (
          <div style={legacyTotalBox}>
            <div>
              <span style={legacyLabel}>기존 총액</span>
              <strong style={legacyAmount}>{Number(form.total_cost || 0).toLocaleString('ko-KR')}원</strong>
            </div>
            <button type="button" onClick={convertToNightlyRates} style={convertBtn}>일별요금으로 전환</button>
          </div>
        )
      )}
    </Modal>
  );
}

const legacyTotalBox = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: 10, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)' };
const legacyLabel = { display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 };
const legacyAmount = { color: 'var(--text)', fontSize: 14 };
const convertBtn = { padding: '6px 12px', border: '1px solid var(--accent-700, #7c3aed)', borderRadius: 4, background: 'rgba(124,58,237,0.08)', color: 'var(--accent-700, #7c3aed)', cursor: 'pointer', fontSize: 12, fontWeight: 700 };
